import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'

import { Box, Grid, Link, TextField, Typography } from '@mui/material'
import FacebookTemplateTextarea from '../../shared/components/ModuleComponents/FacebookTemplateTextarea'
import OpeningHoursDays from '../../shared/components/ModuleComponents/OpeningHoursDays'

import { TextFieldType } from '@professocial/professocialFormComponents/FormComponents/TextFieldComponent'
import DropDownComponent, {
  DropDownElement
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import { getAllFacebookCategories, getFacebookPage } from '../../store/facebook/facebook.selector'
import { FacebookCategory, FacebookPage, OpeningHoursDay } from '@3m5/sm_publish-frontend-api'
import {
  GetAllFacebookCategoriesRequest,
  SaveFacebookPageInformationRequest,
} from '@3m5/sm_publish-frontend-api'
import {
  getAllFacebookCategoriesInitiated,
  saveFacebookPageInformationInitiated
} from '../../store/facebook/facebook.actions'
import { FacebookSettingsRootState } from '../../store/facebookSettingsRoot.reducer'
import { namedRequestInProgress } from '../../store/common/common.selectors'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import { UserSettings } from '@professocialApi/professocial-core-frontend-api'
import { getDirtyValues } from '../../util/getDirtyValues'

interface Props {
  config: FacebookPage | undefined;
  updatePage?: () => void;
  isWizard?: boolean;
  wizardConfirmed?: boolean;
  imprintData?: string;
  user?: UserSettings
}

interface IInfoForm {
  adpWebsite?: string;
  zip?: string;
  telNumber?: string;
  city?: string;
  email?: string;
  street?: string;

  [key: string]: any;
}

type dataFieldsType = {
  name: 'adpWebsite' | 'zip' | 'telNumber' | 'city' | 'email' | 'street'
  label: string
  type: TextFieldType
}

const UserFacebookInfo: React.FC<Props> = (props) => {
  const [categories, setCategories] = useState<DropDownElement[]>()
  const [days, setDays] = useState<OpeningHoursDay[]>([])
  // const [aboutUs, setAboutUs] = useState<string | undefined>('')
  const [longDesc, setLongDesc] = useState<string | undefined>('')

  const { t } = useTranslation()

  const dispatch = useDispatch()
  const dispatchSaveFacebookPage = useCallback((params: SaveFacebookPageInformationRequest) => dispatch(saveFacebookPageInformationInitiated(params)), [dispatch])
  const dispatchGetAllFacebookCategories = useCallback((params: GetAllFacebookCategoriesRequest) => dispatch(getAllFacebookCategoriesInitiated(params)), [dispatch])

  const allFacebookCategories: Array<FacebookCategory> | undefined = useSelector(getAllFacebookCategories)
  const facebookPage: FacebookPage | undefined = useSelector(getFacebookPage)
  const savingFacebookPage: boolean = useSelector((state: FacebookSettingsRootState) => namedRequestInProgress(state, saveFacebookPageInformationInitiated.type))
  let saving = false

  // const shortDeskLength = 155
  const longDescLength = 1500

  const validationSchema = Yup.object().shape({
    adpWebsite: Yup.string(),
    zip: Yup.string(),
    telNumber: Yup.string(),
    city: Yup.string(),
    email: Yup.string().email(`${t('facebookSettingsCommon:emailFalse')}`),
    street: Yup.string(),
  })

  const defaultValues: IInfoForm = {
    adpWebsite: props.config?.customizablePageData?.website,
    zip: props.config?.customizablePageData?.location?.zip,
    telNumber: props.config?.customizablePageData?.phone,
    city: props.config?.customizablePageData?.location?.city,
    street: props.config?.customizablePageData?.location?.street,
    email: props.config?.customizablePageData?.emails && props.config?.customizablePageData?.emails?.length !== 0
      ? props.config?.customizablePageData?.emails[0] // TODO muss später noch auf array geändert werden
      : '',
  }

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
  } = useForm<IInfoForm>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const replacePlaceholderWithUserData = (description: string) => {
    const replacements: any = { vorname: props.user?.firstName, nachname: props.user?.lastName }
    return description?.replace(/\[(.*?)]/g, placeholder => {
      return replacements[placeholder.substring(1, placeholder.length - 1)] || placeholder
    }
    )
  }

  useEffect(() => {
    reset(defaultValues)
  }, [reset, props.config])

  useEffect(() => {
    if (saving) {
      props.updatePage && props.updatePage()
      saving = false
    } else {
      saving = true
    }
  }, [savingFacebookPage])

  useEffect(() => {
    if (props.config) {
      // setAboutUs(props.config.customizablePageData?.about)
      setLongDesc(props.config.customizablePageData?.description)
    }
  }, [props.config])

  useEffect(() => {
    allFacebookCategories && setCategories(configCategories(allFacebookCategories))
  }, [allFacebookCategories])

  const dataFields: dataFieldsType[] = [
    {
      name: 'adpWebsite',
      label: t('facebookSettingsCommon:adpWebsite'),
      type: TextFieldType.text,
    },
    {
      name: 'zip',
      label: t('facebookSettingsCommon:zip'),
      type: TextFieldType.text,
    },
    {
      name: 'telNumber',
      label: t('facebookSettingsCommon:telNumber'),
      type: TextFieldType.text,
    },
    {
      name: 'city',
      label: t('facebookSettingsCommon:city'),
      type: TextFieldType.text,
    },
    {
      name: 'email',
      label: t('facebookSettingsCommon:email'),
      type: TextFieldType.text,
    },
    {
      name: 'street',
      label: t('facebookSettingsCommon:street'),
      type: TextFieldType.text,
    },
  ]

  const configCategories = (categories: FacebookCategory[]) => {
    const array: DropDownElement[] = []
    categories.map((elem: FacebookCategory) => {
      if (elem && elem.id && elem.name) {
        array.push({
          key: elem.id,
          value: elem.name,
        })
      }
    })
    return array
  }

  const saveFacebookPageInfo: SubmitHandler<FieldValues> = data => {
    const dirty = getDirtyValues(dirtyFields, data)

    if (props.config && data && days) {
      const params: SaveFacebookPageInformationRequest = {
        networkPageId: props.config.networkPageId,
        facebookPageCustomInformation: {
          // about: aboutUs,
          description: longDesc || undefined,
          phone: dirty.telNumber || undefined,
          openingHours: { days },
          location: dirty.city || dirty.street || dirty.zip ? { city: dirty.city, street: dirty.street, zip: dirty.zip } : undefined,
          website: dirty.adpWebsite || undefined,
          emails: dirty.email ? [dirty.email] : undefined,
          impressum: props.imprintData || undefined,
        },
      }
      dispatchSaveFacebookPage(params)
    }
  }

  const handleOpeningHours = (data: OpeningHoursDay[]) => {
    setDays(data)
  }

  // const handleAboutUs = (desc: string | undefined) => {
  //   desc ? setAboutUs(desc) : setAboutUs('')
  // }

  const handleLongDesc = (desc: string | undefined) => {
    desc ? setLongDesc(replacePlaceholderWithUserData(desc)) : setLongDesc('')
  }

  useEffect(() => {
    facebookPage && dispatchGetAllFacebookCategories({
      networkPageId: facebookPage.networkPageId,
    })
  }, [facebookPage])

  useEffect(() => {
    props.wizardConfirmed && handleSubmit(saveFacebookPageInfo)()
  }, [props.wizardConfirmed])
  // Test
  return (
    <>
      <Grid container item xs={12} columnSpacing={5}>
        {!props.isWizard &&
          <Grid item xs={12}>
            <Typography>{t('facebookSettingsCommon:infoBoxText')} &nbsp;
              <Link
                href="https://www.facebook.com/3m5dev/about?tab=page_info">{t('facebookSettingsCommon:info')}</Link>.
            </Typography>
          </Grid>}
        <Grid item xs={12}>
          {/* <FacebookTemplateTextarea */}
          {/*  maxLength={shortDeskLength} */}
          {/*  maxRows={4} */}
          {/*  text={aboutUs} */}
          {/*  templateText={t('facebookSettingsCommon:templateShort')} */}
          {/*  label={t('facebookSettingsCommon:shortDesc')} */}
          {/*  updateText={handleAboutUs} */}
          {/* /> */}
          <FacebookTemplateTextarea
            maxLength={longDescLength}
            maxRows={6}
            text={longDesc}
            templateText={t('facebookSettingsCommon:templateLong')}
            label={t('facebookSettingsCommon:detailedDesc')}
            updateText={handleLongDesc}
          />
        </Grid>
        <Grid item xs={12} lg={7}>
          <Typography sx={{ mt: 2, pb: 1 }} variant="h5">{t('facebookSettingsCommon:adressData')}</Typography>
          <>
            {dataFields.map(field => field.type &&
              <Box key={field.label} sx={{ mb: 3, mt: 2 }}>
                <TextField
                  id={field.label}
                  label={field.label}
                  type={field.type}
                  {...register(field.name)}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors[field.name]}
                  helperText={errors[field.name]?.message}
                  size="small"
                  fullWidth
                />
              </Box>,
            )}
            {categories && categories.length > 0 && (
              <Box sx={{ mb: 3, mt: 2 }}>
                <DropDownComponent label={t('facebookSettingsCommon:pageCategory')} size="small"
                  options={categories} value={categories[0].key}/>
              </Box>
            )}
          </>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Typography sx={{ mt: 2, pb: 1 }} variant="h5">{t('facebookSettingsCommon:openHours')}</Typography>
          <OpeningHoursDays openingHours={props.config?.customizablePageData?.openingHours}
            setOpeningHours={handleOpeningHours}/>
        </Grid>
      </Grid>
      {!props.isWizard &&
        <Box sx={{ mt: 4 }}>
          <PreloaderButton
            position='right'
            loading={savingFacebookPage}
            onClick={handleSubmit(saveFacebookPageInfo)}
            text={`${t('facebookSettingsCommon:saveChanges')}`}
          />
        </Box>}
    </>
  )
}
export default UserFacebookInfo
